export const HERO_STAGE_WIDTH = 3840
export const HERO_STAGE_HEIGHT = 1520

export type HeroLayerEffect = 'fade-scale' | 'slide-y' | 'slide-x' | 'grow-y'

export type HeroImageLayer = {
    id: string
    file: string
    x: number
    y: number
    w: number
    h: number
    stack: 'image' | 'tag'
    delayMs: number
    durationMs: number
    easing: string
    effect: HeroLayerEffect
    /** Stage-pixel translation, or unitless scale, depending on `effect`. */
    from: number | null
}

export type HeroPill = {
    id: string
    label: string
    cx: number
    cy: number
    height: number
    fontSize: number
    dot: number
    padL: number
    padR: number
    gap: number
    radius: number
    delayMs: number
    durationMs: number
    easing: string
}

export type HeroPlaceholder = {
    id: string
    x: number
    y: number
    w: number
    h: number
    radius: number
    delayMs: number
    durationMs: number
    easing: string
}

export type HeroConnector = {
    id: string
    d: string
    /** End-dot centre. Omit both when the connector has no end dot. */
    x?: number
    y?: number
    cycles: number
}

export type HeroAnimationConfig = {
    srcDir: string
    layers: readonly HeroImageLayer[]
    pills: readonly HeroPill[]
    placeholders: readonly HeroPlaceholder[]
    connectors: readonly HeroConnector[]
}

const shipmentRows: HeroImageLayer[] = [0, 1, 2, 3, 4].map((index) => ({
    id: `row-${index + 1}`,
    file: `row-${index + 1}.png`,
    x: 2650,
    y: 618 + index * 85,
    w: 777,
    h: 81,
    stack: 'image',
    delayMs: 1150 + index * 700,
    durationMs: 520,
    easing: 'cubic-bezier(.2,.75,.2,1)',
    effect: 'slide-y',
    from: -22,
}))

const analyticsBars: HeroImageLayer[] = (
    [
        [2185, 859, 61, 296],
        [2261, 851, 61, 304],
        [2337, 849, 59, 306],
        [2411, 801, 61, 354],
        [2487, 825, 61, 330],
        [2563, 843, 61, 312],
        [2639, 771, 59, 384],
        [2713, 829, 61, 326],
        [2789, 803, 61, 352],
        [2865, 859, 61, 296],
        [2941, 783, 59, 372],
        [3015, 815, 61, 340],
    ] as const
).map(([x, y, w, h], index) => ({
    id: `bar-${index + 1}`,
    file: `bar-${index + 1}.png`,
    x,
    y,
    w,
    h,
    stack: 'image',
    delayMs: 350 + index * 80,
    durationMs: 650,
    easing: 'cubic-bezier(.2,.75,.2,1)',
    effect: 'grow-y',
    from: null,
}))

const shipmentDataCollectionHeroConfig = {
    srcDir: '/images/hero-animations/shipment-data-collection',
    layers: [
        {
            id: 'tag-air-final',
            file: 'tag-air-final.png',
            x: 2287,
            y: 440,
            w: 245,
            h: 55,
            stack: 'tag',
            delayMs: 900,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        {
            id: 'tag-ocean-final',
            file: 'tag-ocean-final.png',
            x: 2721,
            y: 361,
            w: 245,
            h: 55,
            stack: 'tag',
            delayMs: 1600,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        {
            id: 'tag-sheet-final',
            file: 'tag-sheet-final.png',
            x: 3149,
            y: 361,
            w: 204,
            h: 55,
            stack: 'tag',
            delayMs: 3000,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        {
            id: 'tag-tms-final',
            file: 'tag-tms-final.png',
            x: 2286,
            y: 691,
            w: 163,
            h: 54,
            stack: 'tag',
            delayMs: 2300,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        {
            id: 'tag-erp-final',
            file: 'tag-erp-final.png',
            x: 2313,
            y: 915,
            w: 159,
            h: 54,
            stack: 'tag',
            delayMs: 4300,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        {
            id: 'tag-parcel-final',
            file: 'tag-parcel-final.png',
            x: 2398,
            y: 1127,
            w: 245,
            h: 55,
            stack: 'tag',
            delayMs: 3700,
            durationMs: 340,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.94,
        },
        ...shipmentRows,
    ],
    pills: [
        {
            id: 'tag-air-progress',
            label: 'Uploading',
            cx: 2409.5,
            cy: 467.5,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 900,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'tag-ocean-progress',
            label: 'Uploading',
            cx: 2843.5,
            cy: 388.5,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 1600,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'tag-sheet-progress',
            label: 'Uploading',
            cx: 3251,
            cy: 388.5,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 3000,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'tag-tms-progress',
            label: 'Syncing',
            cx: 2367.5,
            cy: 718,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 2300,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'tag-erp-progress',
            label: 'Syncing',
            cx: 2392.5,
            cy: 942,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 4300,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'tag-parcel-progress',
            label: 'Uploading',
            cx: 2520.5,
            cy: 1154.5,
            height: 51,
            fontSize: 26,
            dot: 11,
            padL: 20,
            padR: 24,
            gap: 12,
            radius: 26,
            delayMs: 3700,
            durationMs: 220,
            easing: 'ease-in',
        },
    ],
    placeholders: [],
    connectors: [
        {
            id: 'conn-ocean',
            d: 'M2857,416 C2857.2,419.5 2857.8,430.0 2858,437 C2858.2,444.0 2858.0,454.5 2858,458',
            x: 2858,
            y: 458,
            cycles: 3,
        },
        {
            id: 'conn-sheet',
            d: 'M3236,416 C3235.8,419.5 3235.3,430.0 3235,437 C3234.7,444.0 3234.2,454.5 3234,458',
            x: 3234,
            y: 458,
            cycles: 6,
        },
        {
            id: 'conn-air',
            d: 'M2452,500 C2455.6,503.2 2467.1,514.5 2473.3,519.4 C2479.5,524.3 2483.8,526.2 2489.2,529.3 C2494.6,532.4 2499.9,535.3 2505.5,538.0 C2511.1,540.7 2516.7,543.0 2522.5,545.3 C2528.3,547.6 2534.4,549.8 2540.4,551.6 C2546.4,553.4 2552.4,554.8 2558.5,556.1 C2564.6,557.4 2570.7,558.6 2576.9,559.5 C2583.1,560.4 2588.0,561.5 2595.7,561.7 C2603.4,562.0 2618.4,561.1 2623,561',
            x: 2623,
            y: 561,
            cycles: 2,
        },
        {
            id: 'conn-tms',
            d: 'M2450,724 C2454.0,724.2 2467.4,724.9 2474.0,725.3 C2480.6,725.7 2483.7,726.0 2489.3,726.3 C2494.9,726.6 2501.5,726.7 2507.7,727.0 C2513.9,727.3 2520.4,727.8 2526.7,728.0 C2533.0,728.2 2539.3,728.4 2545.5,728.5 C2551.7,728.6 2557.4,728.8 2563.6,728.9 C2569.8,729.0 2576.5,729.0 2582.7,729.0 C2588.8,729.0 2593.8,729.0 2600.5,729.0 C2607.2,729.0 2619.2,729.0 2623,729',
            x: 2623,
            y: 729,
            cycles: 5,
        },
        {
            id: 'conn-erp',
            d: 'M2472,915 C2475.6,914.3 2486.8,912.1 2493.3,910.9 C2499.9,909.7 2505.4,908.6 2511.3,907.6 C2517.2,906.6 2523.9,905.7 2529.0,905.0 C2534.1,904.3 2538.4,903.9 2541.9,903.5 C2545.4,903.1 2545.8,903.1 2550.0,902.7 C2554.2,902.3 2561.3,901.5 2567.3,901.0 C2573.3,900.5 2580.0,900.3 2586.0,900.0 C2592.0,899.7 2597.0,899.5 2603.2,899.3 C2609.4,899.1 2619.7,899.0 2623,899',
            x: 2623,
            y: 899,
            cycles: 8,
        },
        {
            id: 'conn-parcel',
            d: 'M2643,1133 C2648.0,1131.8 2664.9,1127.6 2672.8,1125.6 C2680.7,1123.6 2685.8,1122.4 2690.2,1121.2 C2694.6,1120.0 2695.9,1119.5 2699.3,1118.5 C2702.7,1117.5 2706.0,1116.7 2710.6,1115.2 C2715.2,1113.7 2721.6,1111.7 2727.1,1109.6 C2732.6,1107.5 2738.7,1105.0 2743.4,1102.7 C2748.1,1100.4 2752.4,1097.8 2755.5,1095.8 C2758.6,1093.8 2758.8,1095.6 2762.0,1091.0 C2765.2,1086.4 2772.8,1071.8 2775,1068',
            x: 2775,
            y: 1068,
            cycles: 7,
        },
    ],
} as const satisfies HeroAnimationConfig

const emissionsCalculationsHeroConfig = {
    srcDir: '/images/hero-animations/emissions-calculations',
    layers: [
        {
            id: 'leg-1',
            file: 'leg-1.png',
            x: 2182,
            y: 456,
            w: 896,
            h: 176,
            stack: 'image',
            delayMs: 600,
            durationMs: 520,
            easing: 'cubic-bezier(.2,.75,.2,1)',
            effect: 'slide-y',
            from: -22,
        },
        {
            id: 'leg-2',
            file: 'leg-2.png',
            x: 2182,
            y: 644,
            w: 896,
            h: 244,
            stack: 'image',
            delayMs: 1250,
            durationMs: 520,
            easing: 'cubic-bezier(.2,.75,.2,1)',
            effect: 'slide-y',
            from: -22,
        },
        {
            id: 'leg-3',
            file: 'leg-3.png',
            x: 2182,
            y: 900,
            w: 896,
            h: 176,
            stack: 'image',
            delayMs: 1900,
            durationMs: 520,
            easing: 'cubic-bezier(.2,.75,.2,1)',
            effect: 'slide-y',
            from: -22,
        },
        {
            id: 'inferred-tag-leg-1',
            file: 'inferred-tag-leg-1.png',
            x: 2826,
            y: 478,
            w: 226,
            h: 56,
            stack: 'tag',
            delayMs: 900,
            durationMs: 300,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.9,
        },
        {
            id: 'inferred-tag-leg-3',
            file: 'inferred-tag-leg-3.png',
            x: 2826,
            y: 922,
            w: 226,
            h: 56,
            stack: 'tag',
            delayMs: 2200,
            durationMs: 300,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.9,
        },
        {
            id: 'result',
            file: 'result.png',
            x: 2330,
            y: 1090,
            w: 600,
            h: 340,
            stack: 'image',
            delayMs: 2650,
            durationMs: 360,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'fade-scale',
            from: 0.97,
        },
    ],
    pills: [
        {
            id: 'result-progress',
            label: 'Calculating',
            cx: 2630,
            cy: 1212,
            height: 104,
            fontSize: 42,
            dot: 16,
            padL: 38,
            padR: 44,
            gap: 18,
            radius: 999,
            delayMs: 2650,
            durationMs: 220,
            easing: 'ease-in',
        },
    ],
    placeholders: [],
    connectors: [
        {
            id: 'conn-input',
            d: 'M2630,284 L2630,326',
            x: 2630,
            y: 326,
            cycles: 5,
        },
        {
            id: 'conn-result',
            d: 'M2630,1117 L2630,1160',
            x: 2630,
            y: 1117,
            cycles: 5,
        },
    ],
} as const satisfies HeroAnimationConfig

const analyticsReportingHeroConfig = {
    srcDir: '/images/hero-animations/analytics-reporting',
    layers: [
        ...analyticsBars,
        {
            id: 'kpi-1',
            file: 'kpi-1.png',
            x: 2196,
            y: 494,
            w: 264,
            h: 72,
            stack: 'image',
            delayMs: 1350,
            durationMs: 360,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'slide-y',
            from: 8,
        },
        {
            id: 'kpi-2',
            file: 'kpi-2.png',
            x: 2502,
            y: 494,
            w: 258,
            h: 72,
            stack: 'image',
            delayMs: 1500,
            durationMs: 360,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'slide-y',
            from: 8,
        },
        {
            id: 'kpi-3',
            file: 'kpi-3.png',
            x: 2806,
            y: 494,
            w: 260,
            h: 72,
            stack: 'image',
            delayMs: 1650,
            durationMs: 360,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            effect: 'slide-y',
            from: 8,
        },
        {
            id: 'side-cards',
            file: 'side-cards.png',
            x: 3121,
            y: 470,
            w: 719,
            h: 620,
            stack: 'image',
            delayMs: 2150,
            durationMs: 500,
            easing: 'cubic-bezier(.2,.75,.2,1)',
            effect: 'slide-x',
            from: -24,
        },
    ],
    pills: [],
    placeholders: [
        {
            id: 'kpi-1-placeholder',
            x: 2208,
            y: 510,
            w: 150,
            h: 40,
            radius: 10,
            delayMs: 1350,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'kpi-2-placeholder',
            x: 2514,
            y: 510,
            w: 170,
            h: 40,
            radius: 10,
            delayMs: 1500,
            durationMs: 220,
            easing: 'ease-in',
        },
        {
            id: 'kpi-3-placeholder',
            x: 2818,
            y: 510,
            w: 140,
            h: 40,
            radius: 10,
            delayMs: 1650,
            durationMs: 220,
            easing: 'ease-in',
        },
    ],
    connectors: [
        {
            id: 'conn-mode',
            d: 'M1993,278 C1994.7,280.8 1999.3,289.3 2003,295 C2006.7,300.7 2011.0,306.2 2015,312 C2019.0,317.8 2023.0,324.0 2027,330 C2031.0,336.0 2034.8,342.0 2039,348 C2043.2,354.0 2047.5,360.0 2052,366 C2056.5,372.0 2061.0,378.3 2066,384 C2071.0,389.7 2075.5,395.3 2082,400 C2088.5,404.7 2095.7,408.8 2105,412 C2114.3,415.2 2132.5,417.8 2138,419',
            x: 2138,
            y: 419,
            cycles: 4,
        },
        {
            id: 'conn-lane',
            d: 'M1997,1281 C1998.8,1277.5 2004.2,1266.5 2008,1260 C2011.8,1253.5 2016.0,1248.3 2020,1242 C2024.0,1235.7 2028.0,1228.7 2032,1222 C2036.0,1215.3 2040.0,1208.7 2044,1202 C2048.0,1195.3 2051.8,1188.2 2056,1182 C2060.2,1175.8 2064.3,1170.3 2069,1165 C2073.7,1159.7 2078.0,1154.5 2084,1150 C2090.0,1145.5 2096.2,1141.5 2105,1138 C2113.8,1134.5 2131.7,1130.5 2137,1129',
            x: 2137,
            y: 1129,
            cycles: 4,
        },
        {
            id: 'conn-carrier',
            d: 'M2793,272 C2793.3,273.3 2794.3,277.3 2795,280 C2795.7,282.7 2796.2,284.8 2797,288 C2797.8,291.2 2799.5,297.2 2800,299',
            x: 2800,
            y: 299,
            cycles: 4,
        },
        {
            id: 'conn-bu',
            d: 'M2789,1290 C2788.0,1288.8 2785.5,1285.2 2783,1283 C2780.5,1280.8 2776.8,1279.5 2774,1277 C2771.2,1274.5 2768.2,1271.2 2766,1268 C2763.8,1264.8 2762.0,1260.5 2761,1258 C2760.0,1255.5 2760.2,1253.8 2760,1253',
            x: 2760,
            y: 1253,
            cycles: 4,
        },
    ],
} as const satisfies HeroAnimationConfig

const enterpriseShipperRows: HeroImageLayer[] = [0, 1, 2, 3, 4].map((index) => ({
    id: `row-${index + 1}`,
    file: `row-${index + 1}.png`,
    x: 2398,
    y: 807 + index * 72,
    w: 1014,
    h: 70,
    stack: 'image',
    delayMs: 500 + index * 400,
    durationMs: 520,
    easing: 'cubic-bezier(.2,.75,.2,1)',
    effect: 'slide-y',
    from: -22,
}))

const enterpriseShipperKpis: HeroImageLayer[] = (
    [
        ['kpi-suppliers-delivered', 2927, 2550],
        ['kpi-group-emissions', 2412, 2850],
    ] as const
).map(([id, x, delayMs]) => ({
    id,
    file: `${id}.png`,
    x,
    y: 645,
    w: 473,
    h: 62,
    stack: 'image',
    delayMs,
    durationMs: 360,
    easing: 'cubic-bezier(.2,.8,.2,1)',
    effect: 'slide-y',
    from: 8,
}))

const enterpriseShipperPlaceholders: HeroPlaceholder[] = (
    [
        ['kpi-suppliers-delivered-placeholder', 2943, 130, 2550],
        ['kpi-group-emissions-placeholder', 2428, 220, 2850],
    ] as const
).map(([id, x, w, delayMs]) => ({
    id,
    x,
    y: 656,
    w,
    h: 40,
    radius: 10,
    delayMs,
    durationMs: 220,
    easing: 'ease-in',
}))

const enterpriseShippersHeroConfig = {
    srcDir: '/images/hero-animations/enterprise-shippers',
    layers: [...enterpriseShipperRows, ...enterpriseShipperKpis],
    pills: [],
    placeholders: enterpriseShipperPlaceholders,
    connectors: [
        {
            id: 'conn-europe',
            d: 'M2238,560 C2240.7,559.7 2249.3,558.5 2254,558 C2258.7,557.5 2262.0,558.0 2266,557 C2270.0,556.0 2274.0,554.3 2278,552 C2282.0,549.7 2286.0,545.5 2290,543 C2294.0,540.5 2298.0,539.0 2302,537 C2306.0,535.0 2310.0,533.3 2314,531 C2318.0,528.7 2322.0,524.8 2326,523 C2330.0,521.2 2332.7,520.5 2338,520 C2343.3,519.5 2354.7,520.0 2358,520',
            cycles: 5,
        },
        {
            id: 'conn-north-america',
            d: 'M2311,790 C2315.0,790.0 2327.2,790.0 2335,790 C2342.8,790.0 2354.2,790.0 2358,790',
            cycles: 5,
        },
        {
            id: 'conn-asia-pacific',
            d: 'M2290,1019 C2292.5,1019.2 2300.5,1018.3 2305,1020 C2309.5,1021.7 2313.0,1024.5 2317,1029 C2321.0,1033.5 2325.0,1042.3 2329,1047 C2333.0,1051.7 2336.2,1055.2 2341,1057 C2345.8,1058.8 2355.2,1057.8 2358,1058',
            cycles: 5,
        },
    ],
} as const satisfies HeroAnimationConfig

export const shipmentDataCollectionHero: HeroAnimationConfig = shipmentDataCollectionHeroConfig
export const emissionsCalculationsHero: HeroAnimationConfig = emissionsCalculationsHeroConfig
export const analyticsReportingHero: HeroAnimationConfig = analyticsReportingHeroConfig
export const enterpriseShippersHero: HeroAnimationConfig = enterpriseShippersHeroConfig
